import React, { useState } from 'react';
import {
  TextInput,
  Stack,
  Button,
  Paper,
  Title,
  Group,
  NumberInput,
  Textarea,
  Select,
  Switch,
  LoadingOverlay,
  Box,
  Collapse,
  Tooltip,
  Checkbox,
  Alert,
  Text,
  ColorInput,
  Grid
} from '@mantine/core';
import chevronDown from '../../icons/chevron-down.png';
import chevronUp from '../../icons/chevron-up.png';
import { IconInfoCircle } from '@tabler/icons-react';
import { useApp } from '../../context/AppContext';
import _ from 'lodash';
import { useSalon } from '../../context/SalonContext';
import { useMediaQuery } from '@mantine/hooks';

function generateDurationOptions() {
  const options = [];
  for (let minutes = 5; minutes <= 240; minutes += 5) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const label = hours > 0 
      ? `${hours} hr${hours > 1 ? 's' : ''}${mins > 0 ? ` ${mins} min` : ''}`
      : `${mins} min`;
    options.push({ value: `${minutes}`, label });
  }
  return options;
}

function ServiceForm({ onSubmit, onClose, initialData, isEditing = false, serviceTypes = null }) {
  const { createServices, updateServices, deleteStaffService } = useApp();
  const { businessInfo, employees } = useSalon();
  const [isLoading, setIsLoading] = useState(false);
  const initialFormData = {
    id: initialData?.id || null,
    name: initialData?.name || '',
    type: initialData?.service_type || '',
    description: initialData?.description || '',
    duration: `${initialData?.default_duration_mins}`,
    price: initialData?.default_price || null,
    startsAt: initialData?.default_is_variable_price || false,
    default_calendar_event_color: initialData?.default_calendar_event_color || '#3174ad',
    is_self_servicable: initialData?.default_is_self_servicable || (isEditing ? false : true), // Default to true
    employeeSettings: initialData?.servicing_staff?.reduce((acc, staffService) => {
      acc[staffService.staff_id] = {
        id: staffService.id,
        enabled: true,
        price: staffService.price,
        duration: `${staffService.duration_mins}`,
        startsAt: staffService.is_variable_price,
        isOpen: true,
        calendar_event_color: staffService.calendar_event_color || initialData?.default_calendar_event_color || '#3174ad',
        is_self_servicable: staffService.is_self_servicable,
      };
      return acc;
    }, {}) || employees.reduce((acc, employee) => {
      acc[employee.id] = {
        id: null,
        enabled: true,
        price: null,
        duration: null,
        startsAt: false,
        isOpen: false,
        calendar_event_color: null,
        is_self_servicable: isEditing ? false : true,
      };
      return acc;
    }, {})
  };

  const defaultServiceTypes = [
    { value: 'Hair', label: 'Hair' },
    { value: 'Color', label: 'Color' },
  ]

  const serviceTypesOptions = [
    ...(serviceTypes || defaultServiceTypes),
    { value: 'new', label: '+ Add New' }
  ];

  const defaultColorSwatches = [
    '#3174ad', '#ffe06e', '#f7a725', '#fa5252', '#e64980', '#be4bdb',
    '#7950f2', '#4c6ef5', '#228be6', '#15aabf', '#12b886', '#40c057'
  ];
  
  const [formData, setFormData] = useState(initialFormData);
  const [showNewServiceType, setShowNewServiceType] = useState(false);
  const [newServiceType, setNewServiceType] = useState('');
  const [errors, setErrors] = useState({});
  const isMobile = useMediaQuery('(max-width: 450px)');

  const createDefaultSettings = (servicePrice = '', serviceDuration = '', startsAt = false, isSelfServicable = true, staffServiceId = null) => {
    if (staffServiceId) {
      return { 
        id: staffServiceId,
        price: servicePrice,
        duration: serviceDuration,
        startsAt: startsAt, 
        enabled: true,
        isOpen: true,
        calendar_event_color: formData.default_calendar_event_color,
        is_self_servicable: isSelfServicable,
      };
    } else {
      return { 
        price: servicePrice,
        duration: serviceDuration,
        startsAt: startsAt,
        enabled: !initialData,
        isOpen: false,
        calendar_event_color: defaultColorSwatches[0],
        is_self_servicable: isSelfServicable,
      };
    }
  };

  const syncField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      employeeSettings: Object.fromEntries(
        Object.entries(prev.employeeSettings).map(([empId, settings]) => [
          empId,
          {
            ...settings,
            [field]: value
          }
        ])
      )
    }));
  }

  const getEmployeeSettings = (employeeId) => {
    if (!formData?.employeeSettings || !employeeId) {
      const defaultSettings = createDefaultSettings(formData.price, formData.duration, formData.startsAt, formData.is_self_servicable, formData.id);
      return defaultSettings;
    } else {
      const empSetting = formData.employeeSettings[employeeId]
      const defaultSettings = createDefaultSettings(formData.price, formData.duration, formData.startsAt, formData.is_self_servicable);
      return empSetting || defaultSettings;
    }
  };

  const toggleEmployeeDropdown = (employeeId) => {
    const employeeSettings = getEmployeeSettings(employeeId);
    setFormData(prev => ({
      ...prev,
      employeeSettings: {
        ...prev.employeeSettings,
        [employeeId]: {
          ...employeeSettings,
          isOpen: !employeeSettings.isOpen
        }
      }
    }));
  };

  const formatFullPostRequestData = () => {
    return {
      salon_id: businessInfo?.id,
      services: [
        {
          name: formData.name,
          service_type: formData.type,
          description: formData.description,
          default_duration_mins: parseInt(formData.duration),
          default_price: formData.price,
          default_is_variable_price: formData.startsAt,
          default_calendar_event_color: formData.default_calendar_event_color,
          default_is_self_servicable: formData.is_self_servicable,
          staff_services: Object.entries(formData.employeeSettings)
            .filter(([_, settings]) => settings.enabled)
            .map(([empId, settings]) => ({
              staff_id: empId,
              price: settings.price,
              duration_mins: parseInt(settings.duration),
              is_variable_price: settings.startsAt,
              calendar_event_color: settings.calendar_event_color || formData.default_calendar_event_color,
              is_self_servicable: settings.is_self_servicable,
            }))
        }
      ]
    };
  }

  const formatStaffPostRequestData = () => {
    return { // Expected request for POST endpoint
      salon_id: businessInfo?.id,
      staff_services:  Object.entries(formData.employeeSettings)
        .filter(([_, settings]) => settings.enabled)
        .filter(([_, settings]) => !settings.id)
        .map(([empId, settings]) => ({
          salon_service_id: formData.id,
          staff_id: empId,
          price: settings.price,
          duration_mins: parseInt(settings.duration),
          is_variable_price: settings.startsAt,
          calendar_event_color: settings.calendar_event_color || formData.default_calendar_event_color,
          is_self_servicable: settings.is_self_servicable,
        }))
    };
  }

  const formatPutRequestData = () => {
    return {
      salon_services: [
        {
          id: formData.id,
          name: formData.name,
          service_type: formData.type,
          description: formData.description,
          default_duration_mins: parseInt(formData.duration),
          default_price: formData.price,
          default_is_variable_price: formData.startsAt,
          default_calendar_event_color: formData.default_calendar_event_color,
          default_is_self_servicable: formData.is_self_servicable,
        }
      ],
      staff_services: [
        ...Object.entries(formData.employeeSettings)
        .filter(([_, settings]) => settings.enabled)
        .filter(([empId, _]) => initialFormData?.employeeSettings[empId])
        .map(([_, settings]) => ({
          id: settings.id,
          price: settings.price,
          duration_mins: parseInt(settings.duration),
          is_variable_price: settings.startsAt,
          calendar_event_color: settings.calendar_event_color || formData.default_calendar_event_color,
          is_self_servicable: settings.is_self_servicable,
        }))
      ]
    }
  };

  const formatResponse = (response) => {
    const formattedResponse = {
      id: response[0].id,
      name: response[0].name,
      service_type: response[0].service_type,
      description: response[0].description,
      default_duration_mins: response[0].default_duration_mins,
      default_price: response[0].default_price,
      default_is_variable_price: response[0].default_is_variable_price,
      default_calendar_event_color: response[0].default_calendar_event_color,
      default_is_self_servicable: response[0].default_is_self_servicable,
      servicing_staff: response.slice(1).map(staffService => {
        const empId = staffService.staff_id 
          ? staffService.staff_id 
          : Object.entries(formData.employeeSettings).find(([_, settings]) => settings.id === staffService.id)?.[0];
        const emp = employees.find(emp => emp.id === empId);
        return {
          duration_mins: staffService.duration_mins,
          id: staffService.id,
          is_variable_price: staffService.is_variable_price,
          price: staffService.price,
          staff_id: empId,
          staff_name: emp ? `${emp.first_name} ${emp.last_name}` : '',
          calendar_event_color: staffService.calendar_event_color || formData.default_calendar_event_color,
          is_self_servicable: staffService.is_self_servicable,
        };
      }),
        price_range: {
          min: Math.min(...response.slice(1).map(staffService => staffService.price)),
          max: Math.max(...response.slice(1).map(staffService => staffService.price))
        }
    };
    return formattedResponse;
  }

  const scrollToTop = () => {
    document.querySelector('.mantine-Modal-content')?.scrollTo({ top: 0 });
  }

  const createService = async () => {
    const formattedRequestData = formatFullPostRequestData();

    try {
      const response = await createServices(formattedRequestData);

      if (response) {
        const formatedUpdatedResponse = formatResponse(response);
        onSubmit(formatedUpdatedResponse);
        onClose();
      }
    } catch (error) {
      setErrors({topCreateService: error.message});
      setIsLoading(false);
    }
  }

  const editService = async () => {
    if (_.isEqual(formData, initialFormData)) {
      setIsLoading(false);
      onClose();
      return;
    }
    
    const formattedPutRequestData = formatPutRequestData();
    const formattedPostRequestData = formatStaffPostRequestData();

    const updateResponse = await updateServices(formattedPutRequestData);
    const postResponse = formattedPostRequestData.staff_services.length > 0
     ? await createServices(formattedPostRequestData)
     : [];

    const deletedStaffServices = Object.entries(formData.employeeSettings)
      .filter(([staffId, settings]) => {
        const initialSettings = initialFormData.employeeSettings[staffId];
        return initialSettings?.id && !settings.enabled && initialSettings.enabled;
      })
      .map(([_, settings]) => settings.id);


    const deleteResponse = deletedStaffServices.length > 0 ?
      await Promise.all(
        deletedStaffServices.map(async (staffServiceId) => {
          await deleteStaffService(staffServiceId);
        })
      )
      : [];
      
    if (updateResponse && postResponse && deleteResponse) {
      const formatedUpdatedResponse = formatResponse([...updateResponse, ...postResponse]);
      onSubmit(formatedUpdatedResponse);
      onClose();
    }
  }

  const validateForm = () => {
    const newErrors = {};

    // Validate Service Name
    if (!formData.name?.trim()) {
      newErrors.name = 'Service name is required';
    }

    // Validate Service Type
    if (!formData.type?.trim() && formData.type !== '') {
      newErrors.type = 'Service type is required';
    }

    // Validate New Service Type if "new" is selected
    if (formData.type === '' && !newServiceType?.trim()) {
      newErrors.new_service_type = 'New service type is required';
    }

    // Validate Duration
    if (!formData.duration || formData.duration <= 0) {
      newErrors.duration = 'Valid duration is required';
    }

    // Validate Price
    if (!formData.price || formData.price <= 0) {
      newErrors.price = 'Valid price is required';
    }

    // Validate Default Calendar Color
    if (!/^#[0-9A-F]{6}$/i.test(formData.default_calendar_event_color)) {
      newErrors.default_calendar_event_color = 'Valid hex color required (#RRGGBB)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    scrollToTop();
    
    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    setIsLoading(true);
    
    if (!isEditing) {
      createService();
    } else {
      editService();
    }
  };

  return (
    <Stack>
      {isLoading && <LoadingOverlay visible={true} overlayBlur={2} />}
      { errors.topCreateService && <Alert color="red" title="Error creating service">{errors.topCreateService}</Alert>}

      <Paper p={{base: "0", sm: "md"}}>
        <Group position="apart" mb="md">
          <Title order={3}>Service Information</Title>
        </Group>
        <form onSubmit={handleSubmit}>
          <Stack gap={isMobile ? "sm" : "md"}>
            <TextInput
              label="Service Name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={errors.name}
              size={isMobile ? "sm" : "md"}
            />

            <Select
              label={
                <Group gap={4}>
                  <span>Service Type</span>
                  <Text span c="red">*</Text>
                  <Tooltip 
                    label="Used to group and organize services for your customers.  (Ex: Haircut, Color, Manicure, Waxing)" 
                    position="right-start"
                    multiline
                    width={100}
                    styles={{
                      tooltip: {
                        whiteSpace: 'normal',
                        maxWidth: '350px'
                      }
                    }}
                  >
                    <IconInfoCircle size={16} style={{ display: 'inline-block', cursor: 'help' }} />
                  </Tooltip>
                </Group>
              }
              value={showNewServiceType ? 'new' : formData.type}
              onChange={(value) => {
                if (value === 'new') {
                  setShowNewServiceType(true);
                  setFormData({ ...formData, type: '' });
                } else {
                  setShowNewServiceType(false);
                  setFormData({ ...formData, type: value });
                }
              }}
              data={serviceTypesOptions}
              error={errors.type}
              size={isMobile ? "sm" : "md"}
            />
            
            <Collapse in={showNewServiceType}>
              <TextInput
                label={
                  <Group gap={4}>
                    <span>New Service Type</span>
                    <Text span c="red">*</Text>
                  </Group>
                }
                value={newServiceType}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 50);
                  setNewServiceType(value);
                  setFormData({ ...formData, type: value });
                }}
                placeholder="Enter new service type"
                maxLength={50}
                error={errors.new_service_type}
                size={isMobile ? "sm" : "md"}
              />
            </Collapse>

            <Textarea
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              size={isMobile ? "sm" : "md"}
            />

            <Select
              label="Duration"
              required
              value={formData.duration}
              onChange={(value) => syncField('duration', value)}
              data={generateDurationOptions()}
              error={errors.duration}
              size={isMobile ? "sm" : "md"}
            />

            <NumberInput
              label="Price"
              required
              value={formData.price}
              onChange={(value) => syncField('price', value)}
              placeholder="Ex: $20, $30"
              min={0}
              precision={2}
              prefix="$"
              error={errors.price}
              size={isMobile ? "sm" : "md"}
            />

            <Checkbox
              label="Starts At"
              checked={formData.startsAt}
              onChange={(event) => syncField('startsAt', event.currentTarget.checked)}
              size={isMobile ? "sm" : "md"}
            />

            <ColorInput
              label="Default Calendar Color"
              value={formData.default_calendar_event_color}
              onChange={(value) => syncField('default_calendar_event_color', value)}
              error={errors.default_calendar_event_color}
              format="hex"
              swatches={defaultColorSwatches}
              withEyeDropper={false}
            />

            <Grid>
              <Grid.Col span={isMobile ? 12 : 6}>
                <Switch
                  label="Enable Self-Service"
                  checked={formData.is_self_servicable}
                  onChange={(event) => syncField('is_self_servicable', event.currentTarget.checked)}
                />
              </Grid.Col>
            </Grid>

            <Title order={4} mt={isMobile ? "sm" : "md"}>
              <span style={{ marginRight: '.5rem' }}>Performed By</span>
              <Tooltip 
                label="Select which employees can perform this service. Each employee can have their own pricing and duration options." 
                position="right-start"
                multiline
                width={100}
                styles={{
                  tooltip: {
                    whiteSpace: 'normal',
                    maxWidth: '350px'
                  }
                }}
              >
                <IconInfoCircle size={16} style={{ display: 'inline-block', cursor: 'help' }} />
              </Tooltip>
            </Title>
            {employees.map((employee) => (
              <Box key={employee.id} mt={isMobile ? "xs" : "sm"}>
                <Group gap={isMobile ? "sm" : "md"}>
                  <Group>
                    <Switch
                      size={isMobile ? "sm" : "md"}
                      label={`${employee.first_name} ${employee.last_name} - ${employee.title}`}
                      checked={getEmployeeSettings(employee.id).enabled}
                      onChange={(event) => {
                        event.stopPropagation();
                        setFormData(prev => ({
                          ...prev,
                          employeeSettings: {
                            ...prev.employeeSettings,
                            [employee.id]: {
                              ...getEmployeeSettings(employee.id),
                              enabled: event.currentTarget.checked,
                              isOpen: !!event.currentTarget.checked ? true : false
                            }
                          }
                        }));
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Group>
                  <img 
                    src={getEmployeeSettings(employee.id).isOpen ? chevronUp : chevronDown} 
                    alt="toggle" 
                    width="20" 
                    height="20" 
                    style={{ cursor: 'pointer', alignSelf: 'flex-start' }}
                    onClick={() => toggleEmployeeDropdown(employee.id)}
                  />
                </Group>

                <Collapse in={getEmployeeSettings(employee.id).isOpen}>
                  <Box pl={isMobile ? 30 : 50}>
                    <Stack gap="sm">
                      <Group grow align="flex-end" gap={isMobile ? "sm" : "md"}>
                        <Select
                          label="Duration"
                          value={getEmployeeSettings(employee.id).duration}
                          onChange={(value) => {
                            setFormData(prev => ({
                              ...prev,
                              employeeSettings: {
                                ...prev.employeeSettings,
                                [employee.id]: {
                                  ...getEmployeeSettings(employee.id),
                                  duration: value
                                }
                              }
                            }));
                          }}
                          data={generateDurationOptions()}
                          style={{ maxWidth: 160 }}
                        />
                        <ColorInput
                          label="Calendar Color"
                          value={getEmployeeSettings(employee.id).calendar_event_color || formData.default_calendar_event_color}
                          onChange={(value) => {
                            setFormData(prev => ({
                              ...prev,
                              employeeSettings: {
                                ...prev.employeeSettings,
                                [employee.id]: {
                                  ...getEmployeeSettings(employee.id),
                                  calendar_event_color: value
                                }
                              }
                            }));
                          }}
                          format="hex"
                          swatches={defaultColorSwatches}
                          withEyeDropper={false}
                        />
                      </Group>
                      <Group grow align="flex-end" gap={isMobile ? "sm" : "md"}>
                        <NumberInput
                          label="Price"
                          value={getEmployeeSettings(employee.id).price}
                          onChange={(value) => {
                            setFormData(prev => ({
                              ...prev,
                              employeeSettings: {
                                ...prev.employeeSettings,
                                [employee.id]: {
                                  ...getEmployeeSettings(employee.id),
                                  price: value
                                }
                              }
                            }));
                          }}
                          min={0}
                          precision={2}
                          prefix="$"
                          style={{ maxWidth: 160 }}
                        />
                        <Checkbox
                          label="Starts At"
                          checked={getEmployeeSettings(employee.id).startsAt}
                          onChange={(event) => {
                            setFormData(prev => ({
                              ...prev,
                              employeeSettings: {
                                ...prev.employeeSettings,
                                [employee.id]: {
                                  ...getEmployeeSettings(employee.id),
                                  startsAt: event.currentTarget.checked
                                }
                              }
                            }));
                          }}
                          mt={25}
                        />
                      </Group>
                      <Switch
                        label="Enable Self-Service"
                        checked={getEmployeeSettings(employee.id).is_self_servicable}
                        onChange={(event) => {
                          setFormData(prev => ({
                            ...prev,
                            employeeSettings: {
                              ...prev.employeeSettings,
                              [employee.id]: {
                                ...getEmployeeSettings(employee.id),
                                is_self_servicable: event.currentTarget.checked
                              }
                            }
                          }));
                        }}
                      />
                    </Stack>
                  </Box>
                </Collapse>
              </Box>
            ))}

            <Button 
              type="submit" 
              fullWidth 
              mt={isMobile ? "sm" : "xl"} 
              size={isMobile ? "sm" : "md"}
              color="violet"
            >
              {isEditing ? 'Save Changes' : 'Add Service'}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
}

export default ServiceForm;